import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { driver, Driver, DriveStep } from 'driver.js';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserFTUEStatus } from '../../services/userService';
import { markInteractiveCompleted } from './tutorialStorage';
import { createLogger } from '../../utils/logger';

const logger = createLogger('TutorialContext');

interface TutorialContextType {
  startInteractiveTour: (steps: DriveStep[]) => Driver | null;
  startReferenceTour: (tourId: string, steps: DriveStep[]) => Driver | null;
  stopTour: () => void;
  isTourActive: boolean;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

// Shared visual config so every tour (interactive or reference) matches the
// app's blue/white brand theme (see --primary/--foreground in App.scss) and
// the overlay convention used by other modals (e.g. FeedbackModal, UpgradeModal).
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

const TOUR_APPEARANCE = () => {
  const reduceMotion = prefersReducedMotion();
  return {
    animate: !reduceMotion,
    smoothScroll: !reduceMotion,
    allowScroll: false,
    overlayColor: '#000000',
    overlayOpacity: 0.5,
    stagePadding: 6,
    stageRadius: 10,
  } as const;
};

// Delay before auto-starting the interactive tour on mount, so the page has
// time to settle (data loads, animations finish) before anything is highlighted.
export const TOUR_AUTOSTART_DELAY_MS = 1500;

// Delay before starting a reference tour tip right after navigating from
// another page's tour (e.g. session-end -> feedback page). Gives this
// component's own mount effects room to run after the previous page's
// location-change effect has torn down the old tour (see the
// `stopTour()` effect below) — otherwise `isTourActive` can still read
// stale-true and silently block the new tour from starting.
export const REFERENCE_TOUR_POST_NAV_DELAY_MS = 400;

export const useTutorialContext = (): TutorialContextType => {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorialContext must be used within TutorialProvider');
  return ctx;
};

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, token } = useAuth();
  const [isTourActive, setIsTourActive] = useState(false);
  const driverRef = useRef<Driver | null>(null);
  const location = useLocation();

  // Seed localStorage from Keycloak truth so a cleared browser doesn't re-show
  // a tour the user already completed.
  useEffect(() => {
    if (currentUser?.interactive_tutorial_completed) {
      markInteractiveCompleted();
    }
  }, [currentUser]);

  const stopTour = useCallback(() => {
    try {
      driverRef.current?.destroy();
    } catch (err) {
      logger.warn('Error destroying tour', err);
    } finally {
      driverRef.current = null;
      setIsTourActive(false);
    }
  }, []);

  // Destroy any active tour on navigation.
  useEffect(() => {
    stopTour();
  }, [location.pathname, stopTour]);

  // Several pages (e.g. Session Detail) nest their own `overflow-y: auto`
  // panels inside `.layout-content` (a scrollable sidebar, a scrollable
  // feedback column, etc.) — not just the outer `.layout-content` container
  // itself. None of these fire a `window` scroll event, which is all driver.js
  // listens to for repositioning its highlight, so scrolling any of them while
  // a tour is active leaves the highlight box visually desynced from the
  // content behind it. Rather than hunting down every such container by class
  // name (fragile — the next new page would just reintroduce this bug), find
  // every currently-scrollable element on the page and lock it; re-run on
  // every step change too, since tab switches (e.g. Scene Editor, Session
  // Detail's tabbed layout) can mount new scrollable panels mid-tour.
  const lockedScrollersRef = useRef<HTMLElement[]>([]);

  const unlockScrollers = useCallback(() => {
    lockedScrollersRef.current.forEach((el) => el.classList.remove('tutorial-scroll-lock'));
    lockedScrollersRef.current = [];
  }, []);

  const relockScrollers = useCallback(() => {
    unlockScrollers();
    // Scoped to `.layout` (the sidebar + layout-main shell) rather than the
    // whole document — walking every node on the page (headers, portaled
    // modals, unrelated trees) on every tour step is unnecessary and visibly
    // janks pages with large tables.
    const root = document.querySelector('.layout') ?? document.body;
    const scrollers = Array.from(root.querySelectorAll<HTMLElement>('*')).filter((el) => {
      const style = window.getComputedStyle(el);
      return (
        style.overflowY === 'auto' ||
        style.overflowY === 'scroll' ||
        style.overflowX === 'auto' ||
        style.overflowX === 'scroll'
      );
    });
    scrollers.forEach((el) => el.classList.add('tutorial-scroll-lock'));
    lockedScrollersRef.current = scrollers;
  }, [unlockScrollers]);

  useEffect(() => {
    if (isTourActive) {
      relockScrollers();
    } else {
      unlockScrollers();
    }
    return unlockScrollers;
  }, [isTourActive, relockScrollers, unlockScrollers]);

  // driver.js only repositions its highlight/popover on `window` scroll or
  // resize events, but every page's actual scroll container is
  // `.layout-content` (see Layout.scss — `window`/`body` never scroll), and
  // driver.js's own scrollIntoView tracking window is a fixed ~400ms after a
  // step starts. On a long page (most visible on mobile, where stacked
  // layouts require much longer scrolls), a smooth scrollIntoView can still
  // be in flight after that window closes, leaving the highlight frozen at a
  // stale position while `.layout-content` keeps scrolling underneath it.
  // Forward real scroll progress to driver.js's public `refresh()` so it
  // keeps re-measuring the target for as long as any scroll is happening,
  // not just the first 400ms. Capture phase because scroll events on a
  // nested scrollable element don't bubble, but a capture listener on a
  // common ancestor still observes them. `refresh()` is already internally
  // rAF-coalesced (cancels any pending frame and schedules on the next), so
  // wrapping it in another rAF here would only add a frame of lag.
  useEffect(() => {
    if (!isTourActive) return;
    const handleScroll = () => {
      driverRef.current?.refresh();
    };
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
    };
  }, [isTourActive]);

  // Fires before driver.js positions/scrolls to a step's target (see
  // withCenteredHighlight in pageSteps.ts, which relies on this same
  // ordering). If that target sits inside a container we just locked with
  // `overflow: hidden !important` (e.g. a horizontally-scrolling table on a
  // narrow viewport), driver.js's own scrollIntoView can't actually scroll it
  // into view — the highlight ends up pointing at whatever was on-screen
  // before, not the real target. Unlock just that one ancestor for this step;
  // onHighlighted (relockScrollers, below) re-locks everything, including it,
  // once positioning is done, so scroll-lock protection during the step
  // itself is unaffected.
  const unlockAncestorForHighlight = useCallback((element?: Element | null) => {
    element?.closest<HTMLElement>('.tutorial-scroll-lock')?.classList.remove('tutorial-scroll-lock');
  }, []);

  // driver.js resolves each hook as `step.onX || config.onX` — a plain
  // fallback, not a merge. A step that defines its own onHighlightStarted/
  // onHighlighted (e.g. withCenteredHighlight in pageSteps.ts, used on every
  // bottom-of-page pagination step) fully shadows the shared hooks below,
  // silently dropping ancestor-unlock/scroll-relock for exactly that step.
  // Baking the shared hook into every step here — ahead of the step's own
  // hook, so the ancestor is unlocked before pageSteps.ts's forced
  // scrollIntoView runs — keeps this correct regardless of what any given
  // step defines.
  const withSharedTourHooks = useCallback(
    (steps: DriveStep[]): DriveStep[] =>
      steps.map((step) => ({
        ...step,
        onHighlightStarted: (element, s, opts) => {
          unlockAncestorForHighlight(element);
          step.onHighlightStarted?.(element, s, opts);
        },
        // Re-scan for scrollable containers on every step, not just once at
        // tour start — see the scroll-lock effect above for why.
        onHighlighted: (element, s, opts) => {
          relockScrollers();
          step.onHighlighted?.(element, s, opts);
        },
      })),
    [unlockAncestorForHighlight, relockScrollers]
  );

  const startInteractiveTour = useCallback(
    (steps: DriveStep[]): Driver | null => {
      if (isTourActive) return null;
      try {
        const instance = driver({
          ...TOUR_APPEARANCE(),
          steps: withSharedTourHooks(steps),
          onDestroyed: (_element, _step, { state }) => {
            // Only persist completion when the tour was actually finished —
            // dismissing early (Escape, overlay click, close button, or
            // navigating away) also triggers onDestroyed and must not count.
            const reachedLastStep = state.activeIndex === steps.length - 1;
            if (reachedLastStep) {
              markInteractiveCompleted();
              if (currentUser?.id && token) {
                updateUserFTUEStatus(currentUser.id, token, { interactive_tutorial_completed: true }).catch((err) => {
                  logger.warn('Failed to persist interactive tutorial completion', err);
                });
              }
            }
            driverRef.current = null;
            setIsTourActive(false);
          },
        });
        driverRef.current = instance;
        setIsTourActive(true);
        instance.drive();
        return instance;
      } catch (err) {
        logger.warn('Failed to start interactive tour', err);
        driverRef.current = null;
        setIsTourActive(false);
        return null;
      }
    },
    [isTourActive, currentUser, token, withSharedTourHooks]
  );

  const startReferenceTour = useCallback(
    (tourId: string, steps: DriveStep[]): Driver | null => {
      if (isTourActive) return null;
      try {
        const instance = driver({
          ...TOUR_APPEARANCE(),
          steps: withSharedTourHooks(steps),
          onDestroyed: () => {
            driverRef.current = null;
            setIsTourActive(false);
          },
        });
        driverRef.current = instance;
        setIsTourActive(true);
        instance.drive();
        return instance;
      } catch (err) {
        logger.warn(`Failed to start reference tour "${tourId}"`, err);
        driverRef.current = null;
        setIsTourActive(false);
        return null;
      }
    },
    [isTourActive, withSharedTourHooks]
  );

  return (
    <TutorialContext.Provider value={{ startInteractiveTour, startReferenceTour, stopTour, isTourActive }}>
      {children}
    </TutorialContext.Provider>
  );
};
